import React from 'react';
import {
  EyeIcon,
  CalendarIcon,
  UserGroupIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { QRCodeService } from '../utils/qrCodeService';
import { useContent } from '../hooks/useContent';
import VisitsHistogram from './VisitsHistogram';

const StatCard = ({ icon: Icon, label, value, color = 'blue' }) => {
  const { isDark } = useTheme();

  const colorClasses = {
    blue: isDark ? 'text-blue-400 bg-blue-900/20' : 'text-blue-600 bg-blue-50',
    green: isDark ? 'text-green-400 bg-green-900/20' : 'text-green-600 bg-green-50',
    yellow: isDark ? 'text-yellow-400 bg-yellow-900/20' : 'text-yellow-600 bg-yellow-50',
    purple: isDark ? 'text-purple-400 bg-purple-900/20' : 'text-purple-600 bg-purple-50',
  };

  return (
    <div className={`rounded-lg p-4 ${isDark ? 'bg-dark-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex items-center space-x-2 mb-2">
        <div className={`p-1 rounded ${colorClasses[color]}`}>
          <Icon className={`w-4 h-4 ${colorClasses[color].split(' ')[0]}`} />
        </div>
        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{label}</span>
      </div>
      <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
};

const QRCodeStats = ({ stats, qrCode }) => {
  const { isDark } = useTheme();
  const { content } = useContent('qrCodeStats');

  const t = content || {
    noStats: 'Aucune statistique disponible',
    statCards: { totalViews: 'Total des vues', weekScans: 'Nombre de scans cette semaine', monthScans: 'Ce mois', uniqueVisitors: 'Visiteurs uniques' },
    qrInfo: { title: 'Informations du QR Code', createdAt: 'Créé le:', expiresAt: 'Expire le:', never: 'Jamais', status: 'Statut:', active: 'Actif', inactive: 'Inactif' },
    topCountries: { title: 'Top pays', unknown: 'Inconnu', noData: 'Aucune donnée de géolocalisation' },
    devices: { title: 'Appareils' },
    traffic: { title: 'Sources de trafic', direct: 'Direct', noData: 'Aucune donnée de référent' },
    recentActivity: { title: 'Activité récente', unknownCountry: 'Inconnu', unknownDevice: 'Inconnu' },
  };

  if (!stats) {
    return (
      <div className={`p-6 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {t.noStats}
      </div>
    );
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const getTopCountries = () => {
    if (!stats.countries || !Array.isArray(stats.countries)) return [];
    return stats.countries.slice(0, 5);
  };

  const getTopReferrers = () => {
    if (!stats.referrers || !Array.isArray(stats.referrers)) return [];
    return stats.referrers.slice(0, 5);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={EyeIcon} label={t.statCards.totalViews} value={formatNumber(stats.total_visits || 0)} color="blue" />
        <StatCard icon={CalendarIcon} label={t.statCards.weekScans} value={formatNumber(stats.visits_this_week || 0)} color="green" />
        <StatCard icon={CalendarIcon} label={t.statCards.monthScans} value={formatNumber(stats.visits_this_month || 0)} color="yellow" />
        <StatCard icon={UserGroupIcon} label={t.statCards.uniqueVisitors} value={formatNumber(stats.unique_visitors || 0)} color="purple" />
      </div>

      <VisitsHistogram stats={stats} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`rounded-lg p-4 ${isDark ? 'bg-dark-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.qrInfo.title}</h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-start">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>UUID:</span>
              <span className={`font-mono text-xs break-all ml-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{qrCode.uuid}</span>
            </div>
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Type:</span>
              <span className={isDark ? 'text-white' : 'text-gray-900'}>{qrCode.type || 'URL'}</span>
            </div>
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t.qrInfo.createdAt}</span>
              <span className={isDark ? 'text-white' : 'text-gray-900'}>{QRCodeService.formatDateOnly(qrCode.created_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t.qrInfo.expiresAt}</span>
              <span className={isDark ? 'text-white' : 'text-gray-900'}>
                {qrCode.expires_at ? QRCodeService.formatDateOnly(qrCode.expires_at) : t.qrInfo.never}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t.qrInfo.status}</span>
              <span className={`px-2 py-1 rounded text-xs ${
                qrCode.is_active
                  ? isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-600'
                  : isDark ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-600'
              }`}>
                {qrCode.is_active ? t.qrInfo.active : t.qrInfo.inactive}
              </span>
            </div>
          </div>
        </div>

        <div className={`rounded-lg p-4 ${isDark ? 'bg-dark-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <h4 className={`text-sm font-medium mb-3 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <GlobeAltIcon className="w-4 h-4 mr-2" />
            {t.topCountries.title}
          </h4>
          {getTopCountries().length > 0 ? (
            <div className="space-y-2">
              {getTopCountries().map((country, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{country.name || t.topCountries.unknown}</span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{country.visits || 0}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.topCountries.noData}</p>
          )}
        </div>

        {stats.devices && (
          <div className={`rounded-lg p-4 ${isDark ? 'bg-dark-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
            <h4 className={`text-sm font-medium mb-3 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <DevicePhoneMobileIcon className="w-4 h-4 mr-2" />
              {t.devices.title}
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Mobile:</span>
                <span className={isDark ? 'text-white' : 'text-gray-900'}>{stats.devices.mobile || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Desktop:</span>
                <span className={isDark ? 'text-white' : 'text-gray-900'}>{stats.devices.desktop || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Tablet:</span>
                <span className={isDark ? 'text-white' : 'text-gray-900'}>{stats.devices.tablet || 0}%</span>
              </div>
            </div>
          </div>
        )}

        <div className={`rounded-lg p-4 ${isDark ? 'bg-dark-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.traffic.title}</h4>
          {getTopReferrers().length > 0 ? (
            <div className="space-y-2">
              {getTopReferrers().map((referrer, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className={`text-sm truncate ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{referrer.domain || t.traffic.direct}</span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{referrer.visits || 0}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.traffic.noData}</p>
          )}
        </div>
      </div>

      {stats.recent_visits && stats.recent_visits.length > 0 && (
        <div className={`rounded-lg p-4 ${isDark ? 'bg-dark-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.recentActivity.title}</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {stats.recent_visits.slice(0, 10).map((visit, index) => (
              <div key={index} className="flex justify-between items-center text-sm py-1">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{QRCodeService.formatDateTime(visit.timestamp)}</span>
                <div className="flex items-center space-x-2">
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{visit.country || t.recentActivity.unknownCountry}</span>
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>•</span>
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{visit.device || t.recentActivity.unknownDevice}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeStats;
